import React, { useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import './ItemShopPanel2.css';
import './ItemTooltip.css';
import SocketContext from '../../../../../../NewWebSocket/components/WebSocketAdapter';
import DataService from '../../../../../DataService';

// Extend ItemDetails to include categories and itemCategory
interface ExtendedItemDetails extends ItemDetails {
  categories?: string[];
  itemCategory?: string[]; // Add this field to match our JSON structure
  removed?: boolean; // Flag for items that have been removed from the game
}



interface ItemShopPanel2Props {
  isOpen: boolean;
  currentInventory: string[];
  championId: string;
  onItemAdd?: (itemId: string) => void;
  championRole?: string; // For T2 boots access (Mid lane quest: T0=base, T1=normal, T2=upgraded)
  roleQuestCompleted?: boolean; // Whether the role quest is completed
}

export type ItemDetails = {
  id: string;
  name: string;
  stats: Record<string, number>;
  passives?: Array<{
      name: string;
      description: string;
      effects: Record<string, any>;
  }>;
  cost: number;
  tier: string;
  image?: {
      full: string;
  };
  itemCategory?: string[];
  itemGroups?: string[]; // Item group restrictions (e.g. "Fatality", "Lifeline") - only one item per group allowed
  recipe?: {
    buildsInto: string[];
    buildsFrom: string[];
  };
  removed?: boolean; // Flag for items that have been removed from the game
};

// Available categories matching the in-game store
const CATEGORIES = [
  'All Items',
  'Fighter',
  'Marksman',
  'Assassin',
  'Mage',
  'Tank',
  'Support'
];

// Map stat keys to their icon class names
const STAT_ICON_MAP: Record<string, string> = {
  'attackDamage': 'attack-damage-icon',
  'abilityPower': 'ability-power-icon',
  'armor': 'armor-icon',
  'magicResistance': 'magic-resist-icon',
  'attackSpeed': 'attack-speed-icon',
  'abilityHaste': 'ability-haste-icon',
  'health': 'health-icon',
  'mana': 'resource-icon',
  'movementSpeed': 'movement-speed-icon',
  'movementSpeedPercent': 'movement-speed-icon',
  'criticalStrikeChance': 'crit-chance-icon',
  'criticalStrikeDamage': 'crit-damage-icon',
  'critDamage': 'crit-chance-icon',
  'lifeSteal': 'life-steal-icon',
  'omnivamp': 'omnivamp-icon',
  'armorPenetrationPercent': 'armor-penetration-icon',
  'lethality': 'armor-penetration-icon',
  'magicPenetration': 'magic-penetration-icon',
  'magicPenetrationPercent': 'magic-penetration-icon',
  'tenacity': 'tenacity-icon',
  // Health regen variants
  'healthRegen': 'health-regen-icon',
  'baseHealthRegen': 'health-regen-icon',
  'baseHealthRegenPercent': 'health-regen-icon',
  'baseHealthRegeneration': 'health-regen-icon',
  'baseHealthRegenerationPercent': 'health-regen-icon',
  // Mana regen variants
  'manaRegen': 'resource-regen-icon',
  'baseManaRegen': 'resource-regen-icon',
  'baseManaRegenPercent': 'resource-regen-icon',
  'baseManaRegeneration': 'resource-regen-icon',
  'baseManaRegenerationPercent': 'resource-regen-icon',
  'manaRegeneration': 'resource-regen-icon',
  // Heal & Shield Power
  'healShieldPower': 'heal-shield-power-icon',
  'healAndShieldPower': 'heal-shield-power-icon',
  // Gold
  'gold': 'gold-icon',
  'goldPer10': 'gold-icon',
};

// Function to get a display name for a stat key
const getStatDisplayName = (statKey: string): string => {
  const displayNameMap: Record<string, string> = {
    'attackDamage': 'Attack Damage',
    'abilityPower': 'Ability Power',
    'armor': 'Armor',
    'magicResistance': 'Magic Resist',
    'attackSpeed': 'Attack Speed',
    'abilityHaste': 'Ability Haste',
    'health': 'Health',
    'mana': 'Mana',
    'movementSpeed': 'Movement Speed',
    'movementSpeedPercent': 'Movement Speed (%)',
    'criticalStrikeChance': 'Critical Strike',
    'criticalStrikeDamage': 'Critical Damage',
    'critDamage': 'Critical Damage',
    'lifeSteal': 'Life Steal',
    'omnivamp': 'Omnivamp',
    'armorPenetrationPercent': 'Armor Penetration (%)',
    'lethality': 'Lethality',
    'magicPenetration': 'Magic Penetration',
    'magicPenetrationPercent': 'Magic Penetration (%)',
    'tenacity': 'Tenacity',
    // Health regen variants
    'healthRegen': 'Health Regen',
    'baseHealthRegen': 'Base Health Regen',
    'baseHealthRegenPercent': 'Base Health Regen (%)',
    'baseHealthRegeneration': 'Base Health Regen',
    'baseHealthRegenerationPercent': 'Base Health Regen (%)',
    // Mana regen variants
    'manaRegen': 'Mana Regen',
    'baseManaRegen': 'Base Mana Regen',
    'baseManaRegenPercent': 'Base Mana Regen (%)',
    'baseManaRegeneration': 'Base Mana Regen',
    'baseManaRegenerationPercent': 'Base Mana Regen (%)',
    'manaRegeneration': 'Mana Regen',
    // Heal & Shield Power
    'healShieldPower': 'Heal & Shield Power',
    'healAndShieldPower': 'Heal & Shield Power',
    // Gold
    'gold': 'Gold',
    'goldPer10': 'Gold per 10s',
  };
  
  return displayNameMap[statKey] || statKey;
};

const ItemShopPanel2: React.FC<ItemShopPanel2Props> = ({
  isOpen,
  currentInventory,
  championId,
  onItemAdd,
  championRole = 'mid',
  roleQuestCompleted = true,
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const socket = useContext(SocketContext);
  const [activeCategory, setActiveCategory] = useState<string>('All Items');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);

  /** Normalize for fuzzy search: strip apostrophes, periods, spaces, hyphens and lowercase */
  const normalizeForSearch = (str: string): string => {
    return str.toLowerCase().replace(/['\.\s-]/g, '');
  };
  const [hoveredItem, setHoveredItem] = useState<ExtendedItemDetails | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [uniqueContainerId] = useState(() => `item-tooltip-container-${Math.random().toString(36).substr(2, 9)}`);
  const [apiVersion, setApiVersion] = useState<string>('15.9.1');
  
  // Fetch latest API version
  useEffect(() => {
    DataService.fetchLatestVersion().then(setApiVersion);
  }, []);
  
  // Create portal container on mount for tooltips
  useEffect(() => {
    // Create a unique container for this component instance
    const container = document.createElement('div');
    container.id = uniqueContainerId;
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '10000';
    document.body.appendChild(container);
    
    setPortalContainer(container);
    
    // Clean up on unmount
    return () => {
      // Always remove our unique container safely
      try {
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
      } catch (error) {
        console.warn('Error removing tooltip container:', error);
      }
    };
  }, [uniqueContainerId]);
  
  // Add debugging in useEffect to log items data
  useEffect(() => {
    if (gameWideAssets?.itemsDetails) {
      console.log("ItemShopPanel2 - Items data available:", {
        itemCount: gameWideAssets.itemsDetails.length,
        sampleItems: gameWideAssets.itemsDetails.slice(0, 3), // log first 3 items
        imageBaseUrl: itemImageBaseUrl
      });
    } else {
      console.warn("ItemShopPanel2 - No items data available in gameWideAssets");
    }
  }, [gameWideAssets?.itemsDetails]);
  
  // Check if T2 boots are available (Mid lane quest completed)
  const canAccessT2Boots = championRole === 'mid' && roleQuestCompleted;

  // Get all items and apply categories from the JSON data
  const items: ExtendedItemDetails[] = (gameWideAssets?.itemsDetails || [])
    // Filter out removed items
    .filter(item => !(item as ExtendedItemDetails).removed)
    // Filter T2 boots based on quest status
    .filter(item => {
      const extendedItem = item as ExtendedItemDetails;
      // If it's a T2 boot and quest not completed, hide it
      if (extendedItem.tier === 't2boots' && !canAccessT2Boots) {
        return false;
      }
      return true;
    })
    .map(item => {
      // Use the itemCategory field from the JSON file or default to ['All Items']
      const extendedItem = item as ExtendedItemDetails;
      let categories = extendedItem.itemCategory || ['All Items'];
      
      // Always include 'All Items' category
      if (!categories.includes('All Items')) {
        categories = [...categories, 'All Items'];
      }
      
      return {
        ...extendedItem,
        categories: categories
      };
    });
  
  // Filter items by active category and search term (with normalized fuzzy matching)
  const normalizedSearch = normalizeForSearch(searchTerm);
  const filteredItems = items.filter(item => {
    const matchesCategory = item.categories && item.categories.includes(activeCategory);
    const matchesSearch = normalizedSearch === '' || 
      normalizeForSearch(item.name).includes(normalizedSearch);
    return matchesCategory && matchesSearch;
  });
  
  // Group items by tier for the selected category
  const itemsByTier = {
    legendary: filteredItems.filter(item => item.tier === 'legendary'),
    epic: filteredItems.filter(item => item.tier === 'epic'),
    basic: filteredItems.filter(item => item.tier === 'basic'),
    starter: filteredItems.filter(item => item.tier === 'starter'),
    boots: filteredItems.filter(item => item.tier === 'boots'),
    t2boots: filteredItems.filter(item => item.tier === 't2boots'),
  };
  
  // Sort items within each tier: when searching, prioritize "starts with" then alphabetical;
  // otherwise sort by cost descending
  Object.keys(itemsByTier).forEach((tier) => {
    if (normalizedSearch !== '') {
      itemsByTier[tier as keyof typeof itemsByTier].sort((a, b) => {
        const normA = normalizeForSearch(a.name);
        const normB = normalizeForSearch(b.name);
        const aStartsWith = normA.startsWith(normalizedSearch);
        const bStartsWith = normB.startsWith(normalizedSearch);
        // 1. Prioritize names that start with the query
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        // 2. Alphabetical within same group
        return a.name.localeCompare(b.name);
      });
    } else {
      itemsByTier[tier as keyof typeof itemsByTier].sort((a, b) => b.cost - a.cost);
    }
  });
  
  const handleItemAdd = (itemId: string) => {
    if (onItemAdd) {
      // Use the callback provided by parent for local state management
      onItemAdd(itemId);
    } else {
      // Fallback to direct socket emission (legacy behavior)
      if (!socket) return;
      console.log(`[ItemShopPanel2] Adding item: ${itemId} to champion: ${championId}`);
      socket.emit('add-item', { championId, itemId });
    }
  };

  const handleItemRemove = (itemId: string) => {
    if (!socket) return;
    console.log(`[ItemShopPanel2] Removing item: ${itemId} from champion: ${championId}`);
    socket.emit('remove-item', { championId, itemId });
  };
  
  const handleItemMouseEnter = (item: ExtendedItemDetails, event: React.MouseEvent) => {
    console.log('Mouse enter item:', item.name);
    console.log('Item details:', JSON.stringify(item, null, 2));
    console.log('Has stats:', item.stats ? Object.keys(item.stats).length : 'No stats');
    console.log('Has passives:', item.passives ? item.passives.length : 'No passives');
    setMousePosition({ x: event.clientX, y: event.clientY });
    setHoveredItem(item);
  };
  
  const handleItemMouseMove = (event: React.MouseEvent) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };
  
  const handleItemMouseLeave = () => {
    console.log('Mouse leave item');
    setHoveredItem(null);
  };

  // Clean up hovered item when component unmounts
  useEffect(() => {
    return () => {
      setHoveredItem(null);
    };
  }, []);
  
  const itemImageBaseUrl = `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/`;

  // Helper: get itemGroups for an item ID
  const getItemGroups = (itemId: string): string[] => {
    const item = gameWideAssets?.itemsDetails?.find(i => i.id === itemId || parseInt(i.id) === parseInt(itemId));
    return (item as ExtendedItemDetails)?.itemGroups || [];
  };

  // Check if an item conflicts with any item already in the inventory (same item group)
  const getGroupConflict = (itemId: string): string | null => {
    const itemGroupsList = getItemGroups(itemId);
    if (itemGroupsList.length === 0) return null;
    // Check against all items currently in the inventory
    for (const ownedId of currentInventory) {
      for (const group of getItemGroups(ownedId)) {
        if (itemGroupsList.includes(group)) return group;
      }
    }
    return null;
  };
  
  const renderItems = (items: ExtendedItemDetails[], isInventory = false) => {
    return (
      <div className="item-row">
        {items.map((item) => {
          // Debug the image URL
          const imageUrl = `${itemImageBaseUrl}${item.image?.full || 'missing.png'}`;
          // Only mark as "owned" (blocking duplicates) for legendary items
          const isLegendaryItem = item.tier === 'legendary';
          const isOwned = isLegendaryItem && currentInventory.includes(item.id);
          const groupConflict = !isOwned && !isInventory ? getGroupConflict(item.id) : null;
          const isGroupBlocked = !!groupConflict;
          
          return (
            <div
              key={item.id}
              className={`item-box ${isGroupBlocked ? 'group-blocked' : ''}`}
              onDoubleClick={() => {
                if (isInventory) {
                  handleItemRemove(item.id);
                } else if (!isGroupBlocked) {
                  handleItemAdd(item.id);
                }
              }}
              onMouseEnter={(e) => handleItemMouseEnter(item, e)}
              onMouseMove={handleItemMouseMove}
              onMouseLeave={handleItemMouseLeave}
              title={isGroupBlocked ? `Blocked: already own a "${groupConflict}" item` : ''}
            >
              <img 
                src={imageUrl} 
                alt={item.name}
                title="" // Remove default title tooltip
                onError={(e) => {
                  console.error(`Failed to load image for item ${item.name}:`, imageUrl);
                  e.currentTarget.src = `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/7050.png`; // Default item image
                }}
              />
              {!isInventory && <div className="item-price">{item.cost}</div>}
              {isInventory && (
                <button className="sell-button" onClick={() => handleItemRemove(item.id)}>
                  Sell
                </button>
              )}
              {isGroupBlocked && (
                <div className="group-blocked-overlay">✕</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  // Function to calculate tooltip position
  const calculateTooltipPosition = () => {
    if (!hoveredItem) return { top: 0, left: 0 };
    
    // Log tooltip content for debugging
    console.log('Tooltip content for:', hoveredItem.name);
    console.log('Stats:', hoveredItem.stats ? Object.keys(hoveredItem.stats).length : 'none');
    console.log('Passives:', hoveredItem.passives ? hoveredItem.passives.length : 'none');
    
    const tooltipWidth = 300; // Width of our tooltip
    const tooltipHeight = 300; // Estimated height (will vary)
    const padding = 20; // Padding from edge of screen
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Start with default position (to the right of cursor)
    let left = mousePosition.x + 10;
    let top = mousePosition.y + 10;
    
    // Check if tooltip would go off right edge
    if (left + tooltipWidth + padding > viewportWidth) {
      // Position to the left of cursor instead
      left = mousePosition.x - tooltipWidth - 10;
    }
    
    // If tooltip would still be off-screen (narrow viewport), align to edge with padding
    if (left < padding) {
      left = padding;
    }
    
    // Check if tooltip would go off bottom edge
    if (top + tooltipHeight + padding > viewportHeight) {
      // Position above cursor instead
      top = Math.max(mousePosition.y - tooltipHeight - 10, padding);
    }
    
    return { top, left };
  };
  
  if (!isOpen) {
    return null;
  }
  
  return (
    <>
      <div className="item-shop-dropdown">
        <div className="item-shop-header">
          <div className="shop-title-container">
            <h4>Item Shop</h4>
            <div 
              className="info-icon" 
              onMouseEnter={() => setInfoTooltipVisible(true)}
              onMouseLeave={() => setInfoTooltipVisible(false)}
            >
              i
              {infoTooltipVisible && (
                <div className="info-tooltip">
                  Double-click items to buy<br />
                  Right-click items to sell
                </div>
              )}
            </div>
          </div>
          <input
            type="text"
            className="item-search-input"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Category tabs */}
        <div className="item-category-tabs">
          {CATEGORIES.map(category => (
            <button
              key={category}
              className={`category-tab ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        
        {/* Items by tier */}
        <div className="items-by-tier">
          {Object.entries(itemsByTier).map(([tier, tierItems]) => {
            if (tierItems.length === 0) return null;
            
            // Custom tier display names
            const tierDisplayNames: Record<string, string> = {
              legendary: 'Legendary',
              epic: 'Epic',
              basic: 'Basic',
              starter: 'Starter',
              boots: 'Boots',
              t2boots: 'T2 Boots (Mid Quest)',
            };
            
            return (
              <div key={tier} className={`tier-section ${tier === 't2boots' ? 't2boots-section' : ''}`}>
                <h4>{tierDisplayNames[tier] || tier.charAt(0).toUpperCase() + tier.slice(1)}</h4>
                {renderItems(tierItems)}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Tooltip with smart positioning using React portal */}
      {hoveredItem && portalContainer && (() => {
        const { top, left } = calculateTooltipPosition();
        return ReactDOM.createPortal(
          <div 
            style={{
              position: 'fixed',
              top: `${top}px`,
              left: `${left}px`,
              backgroundColor: 'rgba(0, 9, 19, 0.95)',
              color: '#F0E6D2',
              padding: '15px',
              border: '1px solid rgba(120, 90, 40, 0.85)',
              borderRadius: '5px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8), 0 0 1px 1px rgba(255, 255, 255, 0.1)',
              zIndex: 10000,
              width: '300px',
              maxWidth: '300px',
              maxHeight: '500px',
              overflow: 'auto'
            }}
          >
            <div className="item-header">
              <img 
                src={`${itemImageBaseUrl}${hoveredItem.image?.full || 'missing.png'}`} 
                alt={hoveredItem.name}
                className="item-tooltip-image" 
                style={{ width: '48px', height: '48px', marginRight: '10px', border: '1px solid rgba(120, 90, 40, 0.7)', borderRadius: '3px' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ color: '#C89B3C', fontWeight: 'bold', fontSize: '1.15em', marginBottom: '4px' }}>
                  {hoveredItem.name}
                </div>
                <div style={{ color: 'gold', fontSize: '0.9em' }}>
                  <span style={{ fontWeight: 'bold', marginRight: '2px' }}>
                    <div className="stat-icon gold-icon" style={{ display: 'inline-block', marginRight: '2px' }}></div>
                  </span> {hoveredItem.cost}
                </div>
              </div>
            </div>
            
            {/* Stats section */}
            {hoveredItem.stats && Object.keys(hoveredItem.stats).length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                {Object.entries(hoveredItem.stats).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                    <div className={`stat-icon ${STAT_ICON_MAP[key] || 'default-stat-icon'}`}></div>
                    <div style={{ color: '#b2a948', marginRight: '5px' }}>{value}</div>
                    <div style={{ color: '#A09B8C' }}>{getStatDisplayName(key)}</div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Passives section */}
            {hoveredItem.passives && hoveredItem.passives.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {hoveredItem.passives.map((passive, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <div style={{ color: '#F0C96A', fontWeight: 'bold', marginBottom: '3px', fontSize: '0.95em' }}>
                      {passive.name}
                    </div>
                    <div style={{ color: '#A09B8C', fontSize: '0.85em', lineHeight: '1.4' }}>
                      {passive.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>,
          portalContainer
        );
      })()}
    </>
  );
};

export default ItemShopPanel2; 